
import React, { useState, useCallback } from "react";
import { MediaItem } from "./MediaItem";
import { MediaViewer } from "@/components/stories/MediaViewer";
import { MediaUploader } from "./MediaUploader";
import { Media } from "./types";

interface MediaGalleryProps {
  media: Media[];
  onRemove?: (index: number) => void;
  onReorder?: (media: Media[]) => void;
  isEditing?: boolean;
  memorialId?: string;
  onUpload?: (newMedia: Media[]) => void;
}

export const MediaGallery = ({
  media,
  onRemove,
  onReorder,
  isEditing = false,
  memorialId,
  onUpload,
}: MediaGalleryProps) => {
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);

  const handleView = (_mediaUrl: string, index?: number) => {
    setSelectedMediaIndex(index ?? 0);
  };

  const handleFilePreview = useCallback((files: File[]) => {
    // FIXED: Append new files instead of replacing them
    setPreviewFiles(prev => {
      // Optional: prevent duplicates based on name and size
      const newFiles = files.filter(newFile =>
        !prev.some(existingFile =>
          existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );
      return [...prev, ...newFiles];
    });
  }, []);

  const handleUpload = useCallback((newMedia: Media[]) => {
    setPreviewFiles([]); // Clear preview files after successful upload
    if (onUpload) {
      onUpload(newMedia);
    }
  }, [onUpload]);

  const handleRemove = useCallback((index: number) => {
    // Check if this is a preview file or actual media
    if (index < media.length) {
      // It's actual media
      if (onRemove) {
        onRemove(index);
      }
    } else {
      // It's a preview file
      const previewIndex = index - media.length;
      setPreviewFiles(prev => prev.filter((_, i) => i !== previewIndex));
    }
  }, [media.length, onRemove]);

  // Helper function to determine media type from file
  const getMediaTypeFromFile = (file: File): 'image' | 'video' | 'audio' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'image'; // fallback
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {/* Render existing media */}
      {media.map((item, index) => (
        <div key={item.id || `preview-${index}`} className="aspect-square">
          <MediaItem
            mediaUrl={item.url}
            mediaType={item.media_type}
            thumbnailUrl={item.thumbnail_url}
            index={index}
            onView={(_url) => handleView(_url, index)}
            onRemove={isEditing ? handleRemove : undefined}
          />
        </div>
      ))}

      {/* Render preview files */}
      {previewFiles.map((file, index) => {
        const objectUrl = URL.createObjectURL(file);
        const actualIndex = media.length + index;
        
        return (
          <div key={`preview-${index}`} className="aspect-square relative">
            <div className="absolute inset-0 opacity-70">
              <MediaItem
                mediaUrl={objectUrl}
                mediaType={getMediaTypeFromFile(file)}
                index={actualIndex}
                onView={handleView}
                onRemove={isEditing ? handleRemove : undefined}
                disabled={true}
              />
            </div>
            {/* Loading overlay */}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-lg">
              <div className="bg-white/90 px-3 py-1 rounded-full flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-medium text-brand-primary">Uploading...</span>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Media uploader */}
      {isEditing && memorialId && (
        <div className="aspect-square">
          <MediaUploader
            memorialId={memorialId}
            onUpload={handleUpload}
            onFilePreview={handleFilePreview}
            previewFiles={previewFiles}
            multiple={true}
            accept="image/*,video/*,audio/*"
            className="w-full h-full"
          />
        </div>
      )}

      {/* Media Viewer */}
      {selectedMediaIndex !== null && (
        <MediaViewer
          media={media}
          initialIndex={selectedMediaIndex}
          onClose={() => setSelectedMediaIndex(null)}
        />
      )}
    </div>
  );
};
